// ATLAS-5831: CKEditor 5 keeps any HTML (as CKEditor 4 did with `allowedContent: true`) except a short disallow list.
//
// HTML_SUPPORT is the General HTML Support config: allow everything, then disallow the list. GHS alone cannot enforce the
// list, so this guard removes the same things from the DOM before CKEditor converts it:
//   - `a[href]` belongs to the Link feature, which GHS disallow rules never see;
//   - inline SVG and other unknown elements are upcast by GHS's custom-element fallback, which ignores disallow rules;
//   - a disallowed <script> is unwrapped by GHS, which leaves its code behind as a visible paragraph;
//   - HTML embed widgets (`div.raw-html-embed`, the htmlEmbed toolbar button) hold raw HTML that GHS never filters.
//
// The guard wraps the HTML data processor's `_toDom`, which every string input goes through: initial data, setData,
// leaving source editing and clipboard paste. It also sanitizes the value the htmlEmbed command saves.
//
// SYNC: This file exists in two byte-identical copies:
//   Frontend/Atlas.Spa.Backoffice/src/app/shared/widgets/ckeditor-unsafe-html-guard.ts
//   Frontend/Atlas.Spa.Portal/src/app/shared/widgets/ckeditor-unsafe-html-guard.ts
// Keep them in sync; any change goes into both (operator decision 2026-10-13: no shared front-end module).
//
// `_toDom` is private to HtmlDataProcessor. The version test (ckeditor-unsafe-html-guard.spec.ts, U1) fails on any
// ckeditor5 bump: confirm `_toDom` still parses every toView input, then update the version.
//
// No runtime imports: shaped like createAlignedDivTableGuard, a `function` plugin.
import type { GeneralHtmlSupportConfig, PluginConstructor } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';

/** CKEditor version whose HtmlDataProcessor `_toDom` and htmlEmbed command this guard was written against. */
export const UNSAFE_HTML_GUARD_CKEDITOR_VERSION = '48.1.1';
export const UNSAFE_HTML_GUARD_PLUGIN_NAME = 'UnsafeHtmlGuard';

/** Removed with their content. */
export const REMOVED_ELEMENTS = [
  'script',
  'object',
  'embed',
  'applet',
  'meta',
  'base',
  'input',
  'button',
  'select',
  'textarea',
  'foreignobject',
];
/** Removed, keeping their content: a customer form inside one of our Angular forms submits ours. */
export const UNWRAPPED_ELEMENTS = ['form'];
/** Attributes whose value is a URL the browser may load or navigate to. */
export const URL_ATTRIBUTE = /^(href|src|xlink:href|action|formaction|data|poster|background)$/i;
/** Iframe sources kept: https, http and protocol-relative. */
export const IFRAME_SRC = /^(https?:)?\/\//i;
/** Iframe permissions kept; anything else (camera, microphone, geolocation, ...) would prompt in our site's name. */
export const IFRAME_ALLOWED_FEATURES = ['autoplay', 'encrypted-media', 'picture-in-picture', 'fullscreen', 'clipboard-write', 'web-share'];
// SVG animations can set an href to a script URL that the URL check never sees
const SVG_ANIMATION = /^(animate|set|animatemotion|animatetransform)$/;

// browsers ignore leading C0 controls and spaces, and tabs and newlines anywhere in a URL
const URL_IGNORED_LEADING = /^[\u0000- ]+/;
const URL_IGNORED_ANYWHERE = /[\t\n\r]/g;
const UNSAFE_SCHEME = /^(javascript:|vbscript:|data:(?!image\/))/i;

/** General HTML Support config, the same in Backoffice and Portal. */
export const HTML_SUPPORT: GeneralHtmlSupportConfig = {
  allow: [{ name: /.*/, attributes: true, classes: true, styles: true }],
  disallow: [
    { name: /^(script|object|embed|applet|meta|base|form|input|button|select|textarea)$/ },
    { name: /.*/, attributes: /^on/i },
    { name: /.*/, attributes: [{ key: URL_ATTRIBUTE, value: /^[\u0000- ]*(javascript:|vbscript:|data:(?!image\/))/i }] },
    {
      name: 'iframe',
      attributes: [
        { key: 'src', value: /^(?![\u0000- ]*(https?:)?\/\/)/i },
        { key: /^(sandbox|srcdoc)$/i, value: /.*/ },
      ],
    },
  ],
};

/** True for a javascript:, vbscript: or (non-image) data: URL, as the browser would read it. */
export function isUnsafeUrl(value: string): boolean {
  return UNSAFE_SCHEME.test(value.replace(URL_IGNORED_ANYWHERE, '').replace(URL_IGNORED_LEADING, ''));
}

/** Keeps only IFRAME_ALLOWED_FEATURES from an iframe `allow` value; null when none are left. */
export function filterIframeAllow(value: string): string | null {
  const kept = value
    .split(';')
    .map((directive) => directive.trim())
    .filter((directive) => IFRAME_ALLOWED_FEATURES.includes(directive.split(/\s+/)[0].toLowerCase()));
  return kept.length ? kept.join('; ') : null;
}

function sanitizeAttributes(element: Element, name: string): void {
  for (const attribute of Array.from(element.attributes)) {
    const key = attribute.name.toLowerCase();
    if (key.startsWith('on') || key === 'srcdoc' || (URL_ATTRIBUTE.test(key) && isUnsafeUrl(attribute.value))) {
      element.removeAttribute(attribute.name);
    }
  }
  if (name === 'iframe') {
    element.removeAttribute('sandbox');
    const src = element.getAttribute('src');
    if (src !== null && !IFRAME_SRC.test(src.replace(URL_IGNORED_ANYWHERE, '').replace(URL_IGNORED_LEADING, ''))) {
      element.removeAttribute('src');
    }
    const allow = element.getAttribute('allow');
    if (allow !== null) {
      const kept = filterIframeAllow(allow);
      if (kept === null) {
        element.removeAttribute('allow');
      } else {
        element.setAttribute('allow', kept);
      }
    }
  }
}

/** Removes the disallow list from a DOM tree in place, including <template> content. */
export function sanitizeDom(root: DocumentFragment | Element): void {
  for (const element of Array.from(root.querySelectorAll('*'))) {
    const name = element.localName.toLowerCase();
    if (REMOVED_ELEMENTS.includes(name) || (SVG_ANIMATION.test(name) && /href$/i.test(element.getAttribute('attributeName') ?? ''))) {
      element.remove();
      continue;
    }
    sanitizeAttributes(element, name);
    if (name === 'template') {
      sanitizeDom((element as HTMLTemplateElement).content);
    }
    if (UNWRAPPED_ELEMENTS.includes(name)) {
      element.replaceWith(...Array.from(element.childNodes));
    }
  }
}

/** Sanitizes an HTML string without running or loading anything (a <template> parses inertly). */
export function sanitizeHtml(html: string, doc: Document = document): string {
  const template = doc.createElement('template');
  template.innerHTML = html;
  sanitizeDom(template.content);
  return template.innerHTML;
}

const WRAPPED_MARKER = '__wlUnsafeHtmlGuard';

/** Wraps a data processor's `_toDom` so every parsed fragment is sanitized; safe to call twice on one processor. */
export function guardDataProcessor(processor: any): void {
  if (!processor || typeof processor._toDom !== 'function' || processor[WRAPPED_MARKER]) {
    return;
  }
  const toDom = processor._toDom;
  processor._toDom = function (this: any, data: string) {
    const fragment = toDom.call(this, data);
    sanitizeDom(fragment);
    return fragment;
  };
  processor[WRAPPED_MARKER] = true;
}

/** cloud.CKEditor; nothing is needed from it today (kept for shape parity with the other guards). */
export type UnsafeHtmlGuardRuntime = object;

/** Returns a function-style plugin (a `function`, not an arrow: CKEditor `new`s plugin constructors). */
export function createUnsafeHtmlGuard(_ck: UnsafeHtmlGuardRuntime, doc: Document = document): PluginConstructor {
  function UnsafeHtmlGuard(this: any, editor: any) {
    this.editor = editor;
  }
  UnsafeHtmlGuard.pluginName = UNSAFE_HTML_GUARD_PLUGIN_NAME;
  UnsafeHtmlGuard.prototype.init = function (this: any) {
    guardDataProcessor(this.editor.data?.processor);
    guardDataProcessor(this.editor.data?.htmlProcessor);
  };
  // the htmlEmbed command is registered by HtmlEmbedEditing's init, so look for it once every plugin has initialised
  UnsafeHtmlGuard.prototype.afterInit = function (this: any) {
    this.editor.commands?.get('htmlEmbed')?.on(
      'execute',
      (_evt: unknown, args: unknown[]) => {
        if (typeof args[0] === 'string') {
          args[0] = sanitizeHtml(args[0], doc);
        }
      },
      { priority: 'highest' }
    );
  };
  UnsafeHtmlGuard.prototype.destroy = function () {};
  return UnsafeHtmlGuard as unknown as PluginConstructor;
}
